import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { clusterData } from './_utils/clustering';
import { useZoom } from './hooks/useZoom';
import {
  ChartState,
  ChartType,
  ChartBlocksSummary,
  ClusteringProps,
  ZoomProps,
  HighlightingProps,
} from './types';

const ChartContext = createContext<ChartState<unknown> | null>(null);

type ChartProviderProps<T> = PropsWithChildren<{
  data: T[];
  chartBlocks: ChartBlocksSummary;
  chartType: ChartType;
  syncId?: string | number;
  zoomProps?: ZoomProps;
  clusteringProps?: ClusteringProps<T>;
  highlightingProps?: HighlightingProps;
  barSize?: number | string;
}>;

export const ChartProvider = <T,>({
  data: originalData,
  chartBlocks,
  chartType,
  children,
  syncId,
  zoomProps,
  clusteringProps,
  highlightingProps,
  barSize,
}: ChartProviderProps<T>) => {
  const [_highlightedDatapoint, _setHighlightedDatapoint] = useState<string>();

  const _onHighlightChange = useCallback(
    (id: string) => {
      _setHighlightedDatapoint(_highlightedDatapoint === id ? undefined : id);
    },
    [_highlightedDatapoint]
  );

  const {
    zoomedRange,
    refAreaLeft,
    setRefAreaLeft,
    refAreaRight,
    setRefAreaRight,
    hasZoomed,
    zoom,
    resetZoom,
  } = useZoom({
    xAxisDataKey: zoomProps?.xAxisDataKey ?? '',
    minValue: zoomProps?.minValue,
    maxValue: zoomProps?.maxValue,
    useZoomState: zoomProps?.useZoomState,
    onResetZoom: zoomProps?.onResetZoom,
    onZoom: zoomProps?.onZoom,
    chartData: originalData,
    syncId,
  });

  const zoomedData = useMemo(() => {
    if (
      !zoomProps ||
      zoomProps.isServerSide ||
      zoomedRange.from === undefined ||
      zoomedRange.to === undefined
    ) {
      return originalData;
    }

    const dataMapByXAxisKey = new Map(
      originalData.map((d, index) => [
        d[zoomProps.xAxisDataKey as keyof typeof d],
        index,
      ])
    );

    const getItemIndex = (itemKey: number | string) =>
      dataMapByXAxisKey.get(itemKey as T[keyof T]) ?? -1;

    const startIndex = getItemIndex(zoomedRange.from ?? 0);
    const endIndex = getItemIndex(zoomedRange.to ?? 0);
    return originalData.slice(
      startIndex < 0 ? 0 : startIndex,
      endIndex <= startIndex ? originalData.length : endIndex + 1
    );
  }, [originalData, zoomProps, zoomedRange.from, zoomedRange.to]);

  const clusteredData = useMemo(() => {
    if (!clusteringProps) {
      return zoomedData;
    }

    return clusterData({
      chartData: zoomedData,
      dataKey: clusteringProps.dataKey as keyof T,
      groupByKey: clusteringProps.groupByKey as keyof T | undefined,
      isTime: clusteringProps.isTime,
    });
  }, [clusteringProps, zoomedData]);

  return (
    <ChartContext.Provider
      value={{
        originalData,
        data: clusteredData,
        chartBlocks,
        chartType,
        syncId,
        zoomedRange,
        refAreaLeft,
        setRefAreaLeft,
        refAreaRight,
        setRefAreaRight,
        hasZoomed,
        zoom,
        resetZoom,
        zoomEnabled: !!zoomProps,
        showZoomHintButton: zoomProps?.showZoomHintButton ?? true,
        zoomAxisIds: {
          x: zoomProps?.xAxisId,
          y: zoomProps?.yAxisId,
        },
        onHighlightChange:
          highlightingProps?.onHighlightChange ?? _onHighlightChange,
        highlightedDatapoint: highlightingProps?.onHighlightChange
          ? highlightingProps?.highlightedDatapoint
          : _highlightedDatapoint,
        highlightingEnabled: highlightingProps?.enabled,
        isTimeClustering: clusteringProps?.isTime,
        barSize,
      }}
    >
      {children}
    </ChartContext.Provider>
  );
};

export const useChartContext = <T,>() => {
  return useContext(ChartContext) as ChartState<T>;
};
